import React, { FunctionComponent, useEffect, useCallback } from 'react';
import { useParams, Switch, Route, useRouteMatch } from 'react-router';
import { useStore } from 'effector-react';
import { Result, Spin, Button } from 'antd';
import { CaretLeftOutlined } from '@ant-design/icons';
import { goTo } from '@helpers/navigation';
import useTranslate from '@hooks/useTranslate';
import post, { getItem, getComments, getLikes, reset } from '@stores/post';
import PostCard from '@components/PostCard';
import CommentList from '@components/CommentList';
import LikeList from '@components/LikeList';
import css from './PostDetail.module.less';
import posts from '@stores/posts';
import componentContent from '@pages/UserDetailPage/UserInfo/UserInfo.content.json';
import IPost from '@interfaces/IPost';

interface PostDetailProps {}

const PostDetail: FunctionComponent<PostDetailProps> = () => {
  const { postId } = useParams<{ postId: IPost['id'] }>();
  const { preloaders, item } = useStore(post);
  const { preloaders: postListPreloaders } = useStore(posts);
  const { path, url } = useRouteMatch();
  const content = useTranslate(componentContent);

  useEffect(() => {
    getItem({ postId, clientOptions: { isNotFoundRedirect: true } });
    getComments({ postId });
    getLikes({ postId });

    return () => reset();
  }, [postId]);

  const spinning = !!preloaders.getItem || !!postListPreloaders.getList;

  const goBackToUser = useCallback(() => {
    goTo('userDetail', { userId: item?.author?.id });
  }, [item]);

  if (!item && !spinning) {
    return <Result status='404' />;
  }

  return (
    <div className={css.root}>
      <Button
        className={css.goBack}
        type='primary'
        icon={<CaretLeftOutlined />}
        onClick={goBackToUser}
      >
        {content.goBackToUser}
      </Button>
      <div className={css.post}>
        <Spin spinning={spinning}>
          {item ? (
            <PostCard
              isBlocking={preloaders.block === `block_${item.id}`}
              isUnblocking={preloaders.unblock === `unblock_${item.id}`}
              post={item}
            />
          ) : null}
        </Spin>

        <Switch>
          <Route
            exact
            path={path}
          >
            <Spin spinning={!!preloaders.getComments}>
              <CommentList />
            </Spin>
          </Route>
          <Route path={`${url}/likes`}>
            <Spin spinning={!!preloaders.getLikes}>
              <LikeList />
            </Spin>
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default PostDetail;
